import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { klikBiasa } from '../utils/klikBiasa';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, deleteDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getUserRole, ROLES } from '../utils/roles';

const CUSTOM_TABS_DOC = '_custom_tabs'; // Firestore doc ID for custom tab list

// ═══════════════════════════════════════════════════════════
//  AUTOMATION — Peta step rujukan
//  - VIEW mode: tunjuk peta semasa (Firestore override > hardcode)
//  - EDIT mode: edit phase/step, tambah phase baru (amber = baru),
//    drag phase naik/turun, drag step merentasi phase
//  - Simpan → Firestore automation_maps/{id}
//  - Reset → padam Firestore doc, balik hardcode
// ═══════════════════════════════════════════════════════════

const MAPS = [
  {
    id: 'ai16saat', title: 'AI16SAAT', platform: '2-scene · Google Flow',
    usedBy: 'AI16SAAT · Home Niche · Smart Custom (2-scene)', script: 'flow-automation-ai16saat-v3.js',
    phases: [
      { n: '1', title: 'New Project', steps: ['New Project'] },
      { n: '2', title: 'Create Collection', steps: ['Create Collection', 'Rename Collection', 'Setting Collection'] },
      { n: '3', title: 'Create Scene', repeat: '×3 set', steps: ['Create Scene', 'Rename Scene', '✓ Confirm', '9:16', 'Done'] },
      { n: '3', title: 'Jana Gambar', repeat: '×3 set', steps: ['Upload Image', 'Add to Prompt', 'Settings · Image 9:16', 'Paste Prompt', 'Create', 'Tunggu Jana Gambar'] },
      { n: '4', title: 'Batch View', steps: ['Scan Tags', 'Scan Cards (match tag)'] },
      { n: '↓', title: 'Berehat (sebelum jana video)', steps: ['Delay rawak sebelum jana video'] },
      { n: '5', title: 'Jana Video', repeat: '×3 set', steps: ['Animate', 'Video Settings · Veo 3.1', 'Paste Prompt', 'Create', 'Tunggu Jana Video'] },
      { n: '6', title: 'Add to Scene', repeat: '×3 set', steps: [{ label: 'Hover Video → 3-dot', tag: 'fix 21/6' }, 'Add to Scene', 'Pilih Scene', { label: 'Confirm (thumbnail)', tag: 'fix 21/6' }] },
      { n: '7', title: 'Extend Scene 2', repeat: '×3 set', steps: [{ label: 'Buka Scene', tag: 'fix 21/6' }, 'Add Clip', 'Extend', 'Paste Scene 2', 'Submit', { label: 'Done', tag: 'fix 21/6' }] },
      { n: '8', title: 'Download', steps: [{ label: 'Tutup+Buka Scene (retry signal)', tag: 'fix 21/6' }, 'Tunggu Scene 2 Siap', 'Download Combined 16s', 'Done'] },
      { n: '↓', title: 'Berehat antara produk', steps: ['Delay rawak sebelum produk seterusnya'] },
    ],
  },
  {
    id: 'ai8saat', title: 'AI8SAAT + GOYANG', platform: '1-scene · Google Flow',
    usedBy: 'AI8SAAT · GOYANG · Smart Custom (1-scene)', script: 'flow-automation-v3.js  (GOYANG: -goyang-v3, step disalin sama)',
    phases: [
      { n: '1', title: 'Jana Gambar', repeat: '×3 set', steps: ['New Project', 'Create Collection', 'Rename Collection', 'Setting Collection', 'Upload Image', 'Add to Prompt', 'Settings · Image 9:16', 'Paste Prompt', 'Create', 'Tunggu Jana Gambar'] },
      { n: '1.5', title: 'Scan Tags', steps: ['Scan Cards (match tag)'] },
      { n: '↓', title: 'Berehat (sebelum jana video)', steps: ['Delay rawak sebelum jana video'] },
      { n: '2', title: 'Jana Video', repeat: '×3 set', steps: ['Animate', 'Video Settings', 'Paste Prompt', 'Create', 'Tunggu Jana Video'] },
      { n: '4', title: 'Download', steps: ['Download Video'] },
      { n: '↓', title: 'Berehat antara produk', steps: ['Delay rawak sebelum produk seterusnya'] },
    ],
  },
  {
    id: 'chatgpt', title: 'ChatGPT', platform: 'chatgpt.com',
    usedBy: 'Smart Custom (jana kandungan)', script: 'chatgpt-automation.js',
    phases: [
      { n: '1', title: 'Jana Kandungan', steps: ['Type Prompt', 'Upload Image', 'Submit', 'Wait Generation'] },
      { n: '2', title: 'Ambil Hasil', steps: ['Extract JSON', 'Get Conversation Link'] },
    ],
  },
  {
    id: 'tiktok', title: 'TikTok Upload', platform: 'tiktok.com',
    usedBy: 'Semua module', script: 'tiktok-automation.js',
    modes: [
      { name: 'Full Process', desc: 'Showcase + Post (default) — semua step jalan' },
      { name: 'Post Only', desc: 'Post video je — SKIP Product Link (showcase)' },
      { name: 'Showcase Only', desc: 'Tambah showcase je — SKIP Upload Video + Post' },
    ],
    phases: [
      { n: '1', title: 'Upload', steps: ['Buka Upload', 'Upload Video', 'Tunggu Siap'], note: 'skip jika Showcase Only' },
      { n: '2', title: 'Isi Detail', steps: ['Caption', 'Product Link (Showcase)', 'AI Disclosure'], note: 'Product Link skip jika Post Only' },
      { n: '3', title: 'Terbit', steps: ['Set Jadual (opsyenal)', 'Post / Save Draft'], note: 'skip jika Showcase Only' },
    ],
  },
];

function getStepLabel(s) {
  return typeof s === 'object' ? s.label : s;
}

// Returns 'new' | 'original' for a single step chip.
// Set-based: if the step label exists anywhere in origSteps (regardless of position) → original.
// Only marks 'new' if the name itself is not in the original list at all.
function getStepStatus(step, idx, origSteps, phaseStatus) {
  if (phaseStatus === 'new') return 'new';
  const curLabel = getStepLabel(step);
  const origSet = new Set(origSteps.map(getStepLabel));
  return origSet.has(curLabel) ? 'original' : 'new';
}

// Match by title (not index) so reordered phases still find their original.
// "diubah" = step names added or removed. Phase number / repeat / order changes are ignored.
// Returns 'new' | 'modified' | 'original'
function getPhaseStatus(p, i, origPhases) {
  if (p._isNew) return 'new';
  const orig = origPhases.find(op => op.title === p.title);
  if (!orig) return 'new';
  const origSet = new Set((orig.steps || []).map(getStepLabel));
  const curSet  = new Set((p.steps || []).map(getStepLabel));
  const hasAdded   = [...curSet].some(s => !origSet.has(s));
  const hasRemoved = [...origSet].some(s => !curSet.has(s));
  if (hasAdded || hasRemoved) return 'modified';
  return 'original';
}

// Get original phase steps matched by title (not index)
function getOrigSteps(p, origPhases) {
  const orig = origPhases.find(op => op.title === p.title);
  return orig ? orig.steps : [];
}

export default function AutomationView() {
  const role = getUserRole(auth.currentUser?.email);
  const isAdmin = role === ROLES.ADMIN || role === ROLES.MODERATOR;
  /* Tab datang daripada ALAMAT (`/admin/automation/{id}`). Berbeza daripada
     halaman admin lain, senarai tab di sini TIDAK tetap — admin boleh cipta
     tab sendiri (disimpan dalam Firestore), jadi route menerima sebarang id
     dan halaman ini yang memutuskan sah atau tidak. Alamat yang tidak dikenali
     jatuh ke tab pertama, bukan skrin kosong. */
  const navigate = useNavigate();
  const { mapId } = useParams();
  const activeId = mapId || 'ai16saat';
  /* ⛔ Tukar tab = tukar ALAMAT. Tiada lagi setActiveId — menyimpan tab dalam
     keadaan tempatan bermakna muat semula patah balik ke tab pertama. */
  const bukaTab = (id) => navigate(`/admin/automation/${id}`);

  // Custom tabs (admin-created, stored in Firestore)
  const [customTabs, setCustomTabs] = useState([]); // [{id, title}]
  const [addingTab, setAddingTab] = useState(false);
  const [newTabTitle, setNewTabTitle] = useState('');
  const [savingTab, setSavingTab] = useState(false);

  // Firestore override per mapId: { [id]: phases[] | null }
  const [customMaps, setCustomMaps] = useState({});
  const [loadingMap, setLoadingMap] = useState(false);

  // Edit mode
  const [editingMap, setEditingMap] = useState(false);
  const [editPhases, setEditPhases] = useState([]);
  const [savingMap, setSavingMap] = useState(false);

  // Step drag-and-drop (within / across phases)
  const stepDragSrc = useRef(null);       // { pi, si }
  const [stepDragging, setStepDragging] = useState(null);  // { pi, si }
  const [stepDrop, setStepDrop] = useState(null);          // { pi, si }

  // Phase drag-and-drop (reorder phase cards)
  const phaseDragSrc = useRef(null);      // index
  const [phaseDragging, setPhaseDragging] = useState(null); // index
  const [phaseDrop, setPhaseDrop] = useState(null);         // insert-before index

  // Load custom tab list once on mount
  useEffect(() => {
    getDoc(doc(db, 'automation_maps', CUSTOM_TABS_DOC))
      .then(snap => { if (snap.exists()) setCustomTabs(snap.data().tabs || []); })
      .catch(() => {});
  }, []);

  // Load Firestore custom map when tab changes
  useEffect(() => {
    setEditingMap(false);
    if (Object.prototype.hasOwnProperty.call(customMaps, activeId)) return;
    setLoadingMap(true);
    getDoc(doc(db, 'automation_maps', activeId))
      .then(snap => setCustomMaps(prev => ({ ...prev, [activeId]: snap.exists() ? snap.data().phases : null })))
      .catch(() => setCustomMaps(prev => ({ ...prev, [activeId]: null })))
      .finally(() => setLoadingMap(false));
  }, [activeId]);

  if (!isAdmin) {
    return <div style={{ padding: 40, color: '#ef4444' }}>Akses ditolak — admin sahaja.</div>;
  }

  const allMaps = [...MAPS, ...customTabs];
  const active = MAPS.find(m => m.id === activeId) || MAPS[0]; // hardcode entry (may be undefined for custom)
  const isCustomTab = !MAPS.some(m => m.id === activeId);
  const activeCustomTab = isCustomTab ? customTabs.find(t => t.id === activeId) : null;
  const displayPhases = isCustomTab
    ? (customMaps[activeId] || [])
    : (customMaps[activeId] || active.phases);
  const hasCustom = !isCustomTab && !!customMaps[activeId]; // only show diff for hardcode tabs

  // ── Edit map actions ──
  const startEdit = () => {
    setEditPhases(JSON.parse(JSON.stringify(displayPhases)));
    setEditingMap(true);
  };
  const cancelEdit = () => {
    setEditingMap(false);
    setStepDragging(null); setStepDrop(null);
    setPhaseDragging(null); setPhaseDrop(null);
  };
  const saveMap = async () => {
    setSavingMap(true);
    try {
      // Keep _isNew flag in Firestore — used in view mode for color coding
      await setDoc(doc(db, 'automation_maps', activeId), { phases: editPhases, updatedAt: new Date() });
      setCustomMaps(prev => ({ ...prev, [activeId]: editPhases }));
      setEditingMap(false);
    } catch (err) {
      alert('Gagal simpan: ' + err.message);
    } finally {
      setSavingMap(false);
    }
  };
  const resetMap = async () => {
    if (!window.confirm('Reset ke versi asal (buang semua edit)? Ini tak boleh undo.')) return;
    try {
      await deleteDoc(doc(db, 'automation_maps', activeId));
      setCustomMaps(prev => ({ ...prev, [activeId]: null }));
      setEditingMap(false);
    } catch (err) {
      alert('Gagal reset: ' + err.message);
    }
  };

  // ── Edit phase/step helpers ──
  const ep = {
    setN:      (i, v) => setEditPhases(p => p.map((x, j) => j === i ? { ...x, n: v } : x)),
    setTitle:  (i, v) => setEditPhases(p => p.map((x, j) => j === i ? { ...x, title: v } : x)),
    setRepeat: (i, v) => setEditPhases(p => p.map((x, j) => j === i ? { ...x, repeat: v } : x)),
    del:       (i)    => setEditPhases(p => p.filter((_, j) => j !== i)),
    addPhase:  ()     => setEditPhases(p => [...p, { n: '', title: '', steps: [], _isNew: true }]),
    addStep:   (i)    => setEditPhases(p => p.map((x, j) => j === i ? { ...x, steps: [...x.steps, ''] } : x)),
    setStep:   (i, si, v) => setEditPhases(p => p.map((x, j) => j === i ? { ...x, steps: x.steps.map((s, k) => k === si ? v : s) } : x)),
    delStep:   (i, si)    => setEditPhases(p => p.map((x, j) => j === i ? { ...x, steps: x.steps.filter((_, k) => k !== si) } : x)),
    moveStepTo: (srcPi, srcSi, destPi, destSi) => setEditPhases(p => {
      const phases = JSON.parse(JSON.stringify(p));
      const step = phases[srcPi].steps[srcSi];
      phases[srcPi].steps.splice(srcSi, 1);
      const adjSi = (srcPi === destPi && srcSi < destSi) ? destSi - 1 : destSi;
      phases[destPi].steps.splice(adjSi, 0, step);
      return phases;
    }),
    movePhase: (src, dest) => setEditPhases(p => {
      const arr = [...p];
      const [moved] = arr.splice(src, 1);
      const adj = src < dest ? dest - 1 : dest;
      arr.splice(adj, 0, moved);
      return arr;
    }),
  };

  // ── Step drop handler ──
  const handleStepDrop = (destPi, destSi) => {
    if (!stepDragSrc.current) return;
    const { pi: srcPi, si: srcSi } = stepDragSrc.current;
    stepDragSrc.current = null;
    setStepDragging(null);
    setStepDrop(null);
    if (srcPi === destPi && (srcSi === destSi || srcSi === destSi - 1)) return;
    ep.moveStepTo(srcPi, srcSi, destPi, destSi);
  };

  // ── Phase drop handler ──
  const handlePhaseDrop = (destPi) => {
    if (phaseDragSrc.current === null) return;
    const srcPi = phaseDragSrc.current;
    phaseDragSrc.current = null;
    setPhaseDragging(null);
    setPhaseDrop(null);
    if (srcPi === destPi) return;
    ep.movePhase(srcPi, destPi);
  };

  // ── Custom tab handlers ──
  const handleAddTab = async () => {
    const title = newTabTitle.trim();
    if (!title) return;
    const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now().toString(36);
    const newTab = { id, title };
    setSavingTab(true);
    try {
      const ref = doc(db, 'automation_maps', CUSTOM_TABS_DOC);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        await updateDoc(ref, { tabs: arrayUnion(newTab) });
      } else {
        await setDoc(ref, { tabs: [newTab] });
      }
      setCustomTabs(prev => [...prev, newTab]);
      setCustomMaps(prev => ({ ...prev, [id]: [] }));
      bukaTab(id);
      setNewTabTitle('');
      setAddingTab(false);
    } catch (err) {
      alert('Gagal tambah tab: ' + err.message);
    } finally {
      setSavingTab(false);
    }
  };

  const handleDeleteTab = async (tab) => {
    if (!window.confirm(`Padam tab "${tab.title}"? Semua phases dalam tab ini akan hilang.`)) return;
    try {
      await updateDoc(doc(db, 'automation_maps', CUSTOM_TABS_DOC), { tabs: arrayRemove(tab) });
      await deleteDoc(doc(db, 'automation_maps', tab.id)).catch(() => {});
      setCustomTabs(prev => prev.filter(t => t.id !== tab.id));
      setCustomMaps(prev => { const n = { ...prev }; delete n[tab.id]; return n; });
      if (activeId === tab.id) bukaTab('ai16saat');
    } catch (err) {
      alert('Gagal padam: ' + err.message);
    }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
    <div className="sa-wrap">
      <div className="sa-head">
        <h1 className="sa-title">⚙️ Automation — Peta Step</h1>
        <p className="sa-sub">Rujukan visual step-by-step untuk setiap automation script. Klik <b>✏️ Edit</b> untuk ubah suai.</p>
      </div>

      {/* Tabs */}
      {/* Tab = pautan sebenar, supaya klik kanan / Ctrl+klik boleh buka
          tab browser baharu (peraturan sama seperti baris menu sidebar). */}
      <div className="sa-tabs">
        {MAPS.map(m => (
          <a key={m.id} href={`/admin/automation/${m.id}`}
             onClick={(e) => { if (klikBiasa(e)) { e.preventDefault(); bukaTab(m.id); } }}
             className={`sa-tab${m.id === activeId ? ' on' : ''}`}>
            {m.title}
          </a>
        ))}
        {customTabs.map(t => (
          <span key={t.id} className={`sa-tab sa-tab-custom${t.id === activeId ? ' on' : ''}`}>
            <a className="sa-tab-label" href={`/admin/automation/${t.id}`}
               onClick={(e) => { if (klikBiasa(e)) { e.preventDefault(); bukaTab(t.id); } }}>{t.title}</a>
            <button className="sa-tab-del" onClick={() => handleDeleteTab(t)} title="Padam tab">✕</button>
          </span>
        ))}
        {addingTab ? (
          <span className="sa-tab-new-form">
            <input
              className="sa-tab-input"
              autoFocus
              value={newTabTitle}
              placeholder="Nama module…"
              onChange={e => setNewTabTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddTab(); if (e.key === 'Escape') { setAddingTab(false); setNewTabTitle(''); } }}
            />
            <button className="sa-tab-confirm" onClick={handleAddTab} disabled={savingTab || !newTabTitle.trim()}>
              {savingTab ? '…' : '✓'}
            </button>
            <button className="sa-tab-cancel" onClick={() => { setAddingTab(false); setNewTabTitle(''); }}>✕</button>
          </span>
        ) : (
          <button className="sa-tab-add" onClick={() => setAddingTab(true)} title="Tambah tab baru">＋</button>
        )}
      </div>

      {/* Meta */}
      <div className="sa-meta">
        <div><span className="sa-k">Platform</span> {isCustomTab ? (activeCustomTab?.platform || '—') : active.platform}</div>
        <div><span className="sa-k">Guna oleh</span> {isCustomTab ? (activeCustomTab?.usedBy || '—') : active.usedBy}</div>
        <div><span className="sa-k">Script</span> <code>{isCustomTab ? (activeCustomTab?.script || '—') : active.script}</code></div>
      </div>

      {/* Modes (TikTok) */}
      {active.modes && (
        <div className="sa-modes">
          <div className="sa-modes-h">⚙️ Mode (toggle di popup extension)</div>
          {active.modes.map(m => (
            <div key={m.name} className="sa-mode">
              <span className="sa-mode-n">{m.name}</span>
              <span className="sa-mode-d">{m.desc}</span>
            </div>
          ))}
        </div>
      )}

      {/* ══ 2-COL: left = original, right = editable ══ */}
      <div className={`sa-split${isCustomTab ? '' : ' sa-split-cols'}`}>

        {/* LEFT — hardcoded original (standard tabs only) */}
        {!isCustomTab && active && (
          <div className="sa-col sa-col-orig">
            <div className="sa-col-head">📌 Original</div>
            <div className="sa-map">
              {(active.phases || []).map((p, i) => (
                <React.Fragment key={p.title + i}>
                  <div className="sa-phase">
                    <div className="sa-pbar">
                      <span className="sa-pn">{p.n}</span>
                      {p.title}
                      {p.repeat && <span className="sa-rep">{p.repeat}</span>}
                      {p.note && <span className="sa-note">{p.note}</span>}
                    </div>
                    <div className="sa-row">
                      {(p.steps || []).map((s, j) => {
                        const isObjL = typeof s === 'object';
                        const lblL = isObjL ? s.label : s;
                        return (
                          <React.Fragment key={j}>
                            <span className={`sa-chip${isObjL && s.tag ? ' hot' : ''}`}>
                              {lblL}
                              {isObjL && s.tag && <b>★ {s.tag}</b>}
                            </span>
                            {j < p.steps.length - 1 && <i className="sa-arr">→</i>}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                  {i < active.phases.length - 1 && <div className="sa-dn">↓</div>}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* RIGHT — editable / Firestore custom */}
        <div className="sa-col sa-col-edit">
      {/* ── Heading + toolbar ── */}
      <div className="sa-real-head">
        📍 Step Semasa
        {hasCustom && <span className="sa-badge-edited">✏️ diedit</span>}
        {loadingMap && <span className="sa-badge-loading">memuatkan…</span>}
        {!editingMap && <button className="sa-editbtn" onClick={startEdit}>✏️ Edit</button>}
        {editingMap && (
          <>
            <button className="sa-savebtn" onClick={saveMap} disabled={savingMap}>
              {savingMap ? 'Menyimpan…' : '💾 Simpan'}
            </button>
            <button className="sa-cancelbtn" onClick={cancelEdit}>Batal</button>
            {hasCustom && <button className="sa-resetbtn" onClick={resetMap}>↺ Reset ke default</button>}
          </>
        )}
      </div>

      {/* ══ VIEW mode ══ */}
      {!editingMap && isCustomTab && displayPhases.length === 0 && (
        <div className="sa-empty">Belum ada phase. Klik <b>✏️ Edit</b> untuk tambah.</div>
      )}
      {!editingMap && (!isCustomTab || displayPhases.length > 0) && (
        <div className="sa-map">
          {displayPhases.map((p, i) => {
            const phStatus = hasCustom ? getPhaseStatus(p, i, active.phases) : 'original';
            const origSteps = hasCustom ? getOrigSteps(p, active.phases) : [];
            return (
              <React.Fragment key={p.n || i}>
                <div className={`sa-phase${phStatus === 'new' ? ' sa-pv-new' : phStatus === 'modified' ? ' sa-pv-modified' : ''}`}>
                  <div className="sa-pbar">
                    <span className="sa-pn">{p.n}</span>
                    {p.title}
                    {p.repeat && <span className="sa-rep">{p.repeat}</span>}
                    {p.note && <span className="sa-note">{p.note}</span>}
                    {phStatus === 'new' && <span className="sa-tag-new">baru</span>}
                    {phStatus === 'modified' && <span className="sa-tag-mod">diubah</span>}
                  </div>
                  <div className="sa-row">
                    {(p.steps || []).map((s, j) => {
                      const isObj = typeof s === 'object';
                      const label = isObj ? s.label : s;
                      const stStatus = hasCustom ? getStepStatus(s, j, origSteps, phStatus) : 'original';
                      return (
                        <React.Fragment key={j}>
                          <span className={`sa-chip${isObj && s.tag ? ' hot' : stStatus === 'new' ? ' step-new' : ''}`}>
                            {label}
                            {isObj && s.tag && <b>★ {s.tag}</b>}
                          </span>
                          {j < p.steps.length - 1 && <i className="sa-arr">→</i>}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
                {i < displayPhases.length - 1 && <div className="sa-dn">↓</div>}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* ══ EDIT mode ══ */}
      {editingMap && (
        <div className="sa-map">
          {editPhases.map((p, i) => {
            const isNew = !!p._isNew;
            const isPhDragging = phaseDragging === i;
            const showPhBarBefore = phaseDrop === i;
            const showPhBarAfter = i === editPhases.length - 1 && phaseDrop === editPhases.length;

            return (
              <React.Fragment key={i}>
                {/* Phase drop indicator — before */}
                {showPhBarBefore && <div className="sa-phasebar" />}

                <div
                  className={`sa-phase${isNew ? ' sa-enew' : ' sa-ephase'}${isPhDragging ? ' sa-pdragging' : ''}`}
                  onDragOver={(e) => {
                    if (phaseDragSrc.current === null) return;
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();
                    setPhaseDrop(e.clientY < rect.top + rect.height / 2 ? i : i + 1);
                  }}
                  onDrop={(e) => {
                    if (phaseDragSrc.current === null) return;
                    e.preventDefault();
                    handlePhaseDrop(phaseDrop ?? i);
                  }}
                >
                  {/* Phase header — drag handle + editable fields */}
                  <div
                    className="sa-pbar sa-pbar-e"
                    draggable
                    onDragStart={(e) => {
                      phaseDragSrc.current = i;
                      setPhaseDragging(i);
                      e.dataTransfer.effectAllowed = 'move';
                      e.stopPropagation();
                    }}
                    onDragEnd={() => {
                      phaseDragSrc.current = null;
                      setPhaseDragging(null);
                      setPhaseDrop(null);
                    }}
                  >
                    <span className="sa-handle" title="Drag untuk susun semula">⠿</span>
                    <input
                      className={`sa-enuminput${isNew ? ' new' : ''}`}
                      value={p.n || ''}
                      placeholder="no."
                      onChange={(e) => ep.setN(i, e.target.value)}
                      title="Nombor phase"
                    />
                    <input
                      className={`sa-etitleinput${isNew ? ' new' : ''}`}
                      value={p.title || ''}
                      placeholder="Nama phase…"
                      onChange={(e) => ep.setTitle(i, e.target.value)}
                    />
                    <input
                      className="sa-erepeatinput"
                      value={p.repeat || ''}
                      placeholder="ulang? (cth ×3 set)"
                      onChange={(e) => ep.setRepeat(i, e.target.value)}
                    />
                    <button className="sa-x" style={{ marginLeft: 'auto' }} onClick={() => ep.del(i)} title="Buang phase">✕</button>
                  </div>

                  {/* Steps — draggable */}
                  <div
                    className="sa-row"
                    onDragOver={(e) => { if ((p.steps || []).length === 0 && stepDragSrc.current) { e.preventDefault(); setStepDrop({ pi: i, si: 0 }); } }}
                    onDrop={(e) => { if ((p.steps || []).length === 0 && stepDragSrc.current) { e.preventDefault(); handleStepDrop(i, 0); } }}
                  >
                    {(p.steps || []).map((s, j) => {
                      const label = typeof s === 'object' ? s.label : s;
                      const isStepping = stepDragging?.pi === i && stepDragging?.si === j;
                      const showBarBefore = stepDrop?.pi === i && stepDrop?.si === j;
                      const showBarAfter  = stepDrop?.pi === i && stepDrop?.si === j + 1;
                      return (
                        <React.Fragment key={j}>
                          {showBarBefore && <span className="sa-stepbar" />}
                          <span
                            className={`sa-chip sa-dchip sa-sdrag${isStepping ? ' sa-dragging' : ''}`}
                            draggable
                            onDragStart={(e) => {
                              stepDragSrc.current = { pi: i, si: j };
                              setStepDragging({ pi: i, si: j });
                              e.dataTransfer.effectAllowed = 'move';
                              e.stopPropagation();
                            }}
                            onDragOver={(e) => {
                              if (!stepDragSrc.current) return;
                              e.preventDefault(); e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const si = e.clientX < rect.left + rect.width / 2 ? j : j + 1;
                              setStepDrop({ pi: i, si });
                            }}
                            onDrop={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              handleStepDrop(i, stepDrop?.pi === i ? stepDrop.si : j);
                            }}
                            onDragEnd={() => {
                              stepDragSrc.current = null;
                              setStepDragging(null);
                              setStepDrop(null);
                            }}
                          >
                            <input
                              className="sa-sinput"
                              style={{ width: ((label.length || 9) + 1) + 'ch' }}
                              value={label}
                              placeholder="nama step…"
                              onChange={(e) => ep.setStep(i, j, e.target.value)}
                            />
                            <button className="sa-x sa-xs" onClick={() => ep.delStep(i, j)} title="Buang step">✕</button>
                          </span>
                          {!showBarAfter && j < p.steps.length - 1 && <i className="sa-arr">→</i>}
                          {showBarAfter && <span className="sa-stepbar" />}
                        </React.Fragment>
                      );
                    })}
                    {stepDrop?.pi === i && stepDrop?.si === p.steps.length && p.steps.length > 0 && <span className="sa-stepbar" />}
                    <button
                      className="sa-addstep"
                      onDragOver={(e) => { if (stepDragSrc.current) { e.preventDefault(); setStepDrop({ pi: i, si: (p.steps || []).length }); } }}
                      onDrop={(e) => { if (stepDragSrc.current) { e.preventDefault(); handleStepDrop(i, (p.steps || []).length); } }}
                      onClick={() => ep.addStep(i)}
                    >＋ step</button>
                  </div>
                </div>

                {/* Phase drop indicator — after last */}
                {showPhBarAfter && <div className="sa-phasebar" />}

                {/* Divider arrow (only when no phase drop bar follows) */}
                {i < editPhases.length - 1 && phaseDrop !== i + 1 && <div className="sa-dn">↓</div>}
              </React.Fragment>
            );
          })}

          <button className="sa-addgroup" style={{ marginTop: 10 }} onClick={ep.addPhase}>
            ＋ Tambah Phase Baru
          </button>
        </div>
      )}
        </div>{/* end right col */}
      </div>{/* end sa-split */}

      <style>{`
        .sa-wrap{padding:24px 32px 80px;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;color:#1f2430}
        .sa-split{display:grid;gap:24px}
        .sa-split-cols{grid-template-columns:1fr 1fr;align-items:start}
        .sa-col{min-width:0;border:1.5px solid #e6e3ee;border-radius:16px;padding:16px 18px;background:#faf9fc}
        .sa-col-orig{opacity:.8;background:#f5f3fb}
        .sa-col-head{font-size:10.5px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px;padding-bottom:8px;border-bottom:2px solid #ede9f6}
        .sa-head{margin-bottom:18px}
        .sa-title{font-size:20px;font-weight:800;margin:0 0 4px;letter-spacing:-.02em}
        .sa-sub{font-size:12.5px;color:#6b7280;margin:0;line-height:1.55}

        /* Tabs */
        .sa-tabs{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px}
        .sa-tab{font-size:12.5px;font-weight:600;color:#374151;background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:8px 12px;cursor:pointer;transition:border-color .12s;text-decoration:none;display:inline-block}
        .sa-tab:hover{border-color:#c4b5fd}
        .sa-tab.on{background:#7c3aed;border-color:#7c3aed;color:#fff}
        /* Custom tabs */
        .sa-tab-custom{display:inline-flex;align-items:center;gap:0;border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;background:#fff}
        .sa-tab-custom.on{background:#7c3aed;border-color:#7c3aed}
        .sa-tab-label{font-size:12.5px;font-weight:600;color:#374151;background:transparent;border:none;padding:8px 8px 8px 12px;cursor:pointer;text-decoration:none;display:inline-block}
        .sa-tab-custom.on .sa-tab-label{color:#fff}
        .sa-tab-del{border:none;background:transparent;color:#c4b5fd;font-size:10px;padding:0 8px;cursor:pointer;line-height:1}
        .sa-tab-del:hover{color:#dc2626}
        .sa-tab-custom.on .sa-tab-del{color:rgba(255,255,255,.5)}
        .sa-tab-custom.on .sa-tab-del:hover{color:#fff}
        /* New tab inline form */
        .sa-tab-new-form{display:inline-flex;align-items:center;gap:4px;border:1px dashed #a855f7;border-radius:10px;padding:4px 8px;background:#faf5ff}
        .sa-tab-input{border:none;background:transparent;font-size:12.5px;font-weight:600;color:#374151;outline:none;width:120px}
        .sa-tab-input::placeholder{color:#c4b5fd}
        .sa-tab-confirm{border:none;background:#7c3aed;color:#fff;font-size:11px;font-weight:700;border-radius:6px;padding:2px 8px;cursor:pointer}
        .sa-tab-confirm:disabled{opacity:.5;cursor:not-allowed}
        .sa-tab-cancel{border:none;background:transparent;color:#b9a0cf;font-size:11px;cursor:pointer;padding:0 2px}
        /* Add tab button */
        .sa-tab-add{font-size:16px;font-weight:400;color:#a855f7;background:#faf5ff;border:1px dashed #d8b4fe;border-radius:10px;padding:6px 12px;cursor:pointer;line-height:1}
        .sa-tab-add:hover{background:#f3e8ff;border-color:#a855f7}
        /* Empty state */
        .sa-empty{font-size:13px;color:#9a93ad;background:#faf9fc;border:1px dashed #e6dcfa;border-radius:12px;padding:24px;text-align:center;margin-bottom:16px}

        /* Meta */
        .sa-meta{display:flex;flex-direction:column;gap:5px;background:#faf9fc;border:1px solid #ede9f6;border-radius:12px;padding:12px 14px;margin-bottom:18px;font-size:12.5px;color:#374151}
        .sa-k{display:inline-block;min-width:78px;font-weight:700;color:#7c3aed;font-size:10.5px;text-transform:uppercase;letter-spacing:.03em}
        .sa-meta code{font-size:12px;background:#f3f0fa;padding:1px 6px;border-radius:5px;color:#5b21b6}

        /* Modes */
        .sa-modes{border:1px solid #e6e3ee;background:#faf9fc;border-radius:12px;padding:11px 13px;margin-bottom:18px}
        .sa-modes-h{font-size:11px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:.03em;margin-bottom:8px}
        .sa-mode{display:flex;gap:8px;align-items:baseline;padding:3px 0;font-size:12px;line-height:1.45}
        .sa-mode-n{font-weight:700;color:#5b21b6;min-width:112px;flex-shrink:0}
        .sa-mode-d{color:#4b5563}

        /* Heading */
        .sa-real-head{margin-top:8px;font-size:12.5px;font-weight:700;color:#374151;margin-bottom:12px;display:flex;flex-wrap:wrap;gap:8px;align-items:center}
        .sa-badge-edited{font-size:10px;font-weight:600;color:#92400e;background:#fef3c7;padding:2px 8px;border-radius:999px}
        .sa-badge-loading{font-size:10px;color:#9a93ad;background:#f3f0fa;padding:2px 8px;border-radius:999px}
        .sa-editbtn{font-size:11px;font-weight:700;padding:4px 12px;border-radius:8px;border:1px solid #c4b5fd;background:#f5f1fe;color:#7c3aed;cursor:pointer;margin-left:auto}
        .sa-editbtn:hover{background:#ede9fe}
        .sa-savebtn{font-size:11px;font-weight:700;padding:4px 12px;border-radius:8px;border:none;background:#7c3aed;color:#fff;cursor:pointer;margin-left:auto}
        .sa-savebtn:hover{background:#6d28d9}
        .sa-savebtn:disabled{opacity:.6;cursor:not-allowed}
        .sa-cancelbtn{font-size:11px;font-weight:600;padding:4px 12px;border-radius:8px;border:1px solid #e5e7eb;background:#fff;color:#6b7280;cursor:pointer}
        .sa-resetbtn{font-size:11px;font-weight:600;padding:4px 12px;border-radius:8px;border:1px solid #fca5a5;background:#fff;color:#dc2626;cursor:pointer}
        .sa-resetbtn:hover{background:#fee2e2}

        /* Map shared */
        .sa-map{display:flex;flex-direction:column}
        .sa-dn{text-align:center;color:#d1cde0;font-size:15px;padding:1px 0}
        .sa-arr{color:#b6b2c4;font-style:normal;font-size:12px}

        /* Phase card — view */
        .sa-phase{border:1px solid #e6e3ee;border-radius:14px;padding:11px 13px;background:#fff}
        .sa-pbar{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;margin-bottom:10px;color:#1f2430}
        .sa-pn{display:inline-flex;align-items:center;justify-content:center;min-width:24px;height:22px;padding:0 7px;border-radius:999px;background:#7c3aed;color:#fff;font-size:11px;font-weight:700;flex-shrink:0}
        .sa-rep{font-size:10.5px;font-weight:600;color:#7c3aed;background:#f1ebfb;padding:2px 8px;border-radius:999px}
        .sa-note{font-size:10px;font-weight:500;color:#9a93ad;font-style:italic}
        .sa-row{display:flex;flex-wrap:wrap;align-items:center;gap:6px}

        /* Chips */
        .sa-chip{font-size:11.5px;font-weight:500;color:#374151;background:#fff;border:1px solid #e2e0e8;border-radius:9px;padding:6px 10px;white-space:nowrap}
        .sa-chip.hot{border-color:#7c3aed;background:#f5f1fe;color:#5b21b6;font-weight:650}
        .sa-chip.hot b{font-size:9px;font-weight:700;color:#fff;background:#7c3aed;padding:1px 5px;border-radius:6px;margin-left:5px}

        /* View mode — phase status colors (new + modified = same look, badge text differs) */
        .sa-pv-new{border-color:#fcd34d!important;background:#fffdf5}
        .sa-pv-modified{border-color:#fcd34d!important;background:#fffdf5}
        .sa-tag-new{font-size:9.5px;font-weight:700;color:#fff;background:#d97706;padding:1px 7px;border-radius:999px;margin-left:4px}
        .sa-tag-mod{font-size:9.5px;font-weight:700;color:#d97706;background:#fef3c7;border:1px solid #fcd34d;padding:1px 7px;border-radius:999px;margin-left:4px}
        /* Step chip status colors */
        .sa-chip.step-new{background:#fef3c7;border-color:#fbbf24;color:#92400e;font-weight:600}
        .sa-chip.step-mod{background:#fffbeb;border-color:#fcd34d;color:#78350f}
        /* Edit mode — original phase */
        .sa-ephase{border-color:#c4b5fd!important;background:#fdfaff}
        /* Edit mode — NEW phase (amber) */
        .sa-enew{border:1px solid #fbbf24!important;background:#fffbeb}
        .sa-enew .sa-pbar{color:#92400e}

        /* Phase drag */
        .sa-pdragging{opacity:.35}
        .sa-phasebar{height:3px;background:#7c3aed;border-radius:2px;margin:4px 0}
        .sa-pbar-e{cursor:grab;user-select:none}
        .sa-pbar-e:active{cursor:grabbing}
        .sa-handle{font-size:14px;color:#c4b5fd;flex-shrink:0;cursor:grab;line-height:1}

        /* Edit mode phase header inputs */
        .sa-enuminput{width:42px;border:1px solid #e2e8f0;background:#f8fafc;font-size:11px;font-weight:700;color:#7c3aed;border-radius:6px;padding:3px 6px;outline:none;text-align:center;flex-shrink:0}
        .sa-enuminput:focus{border-color:#c4b5fd;background:#fff}
        .sa-enuminput::placeholder{color:#c4b5fd;font-weight:400}
        .sa-enuminput.new{color:#d97706;border-color:#fcd34d}
        .sa-etitleinput{flex:1;min-width:100px;border:none;border-bottom:1px solid #c4b5fd;background:transparent;font-size:13px;font-weight:700;color:#1f2430;outline:none;padding:2px 4px}
        .sa-etitleinput:focus{border-bottom-color:#7c3aed}
        .sa-etitleinput::placeholder{color:#b6b2c4;font-weight:400}
        .sa-etitleinput.new{border-bottom-color:#fbbf24;color:#92400e}
        .sa-erepeatinput{width:110px;border:1px solid #e2e8f0;background:#f8fafc;font-size:11px;color:#7c3aed;border-radius:6px;padding:3px 6px;outline:none}
        .sa-erepeatinput:focus{border-color:#c4b5fd;background:#fff}
        .sa-erepeatinput::placeholder{color:#c4b5fd;font-size:10px}

        /* Step chips edit mode */
        .sa-dchip{display:inline-flex;align-items:center;gap:1px;padding:4px 5px 4px 10px;border-style:dashed;border-color:#d8b4fe;background:#fff}
        .sa-sdrag{cursor:grab}
        .sa-sdrag:active{cursor:grabbing}
        .sa-dragging{opacity:.35;border-style:dashed!important}
        .sa-sinput{border:none;background:transparent;font-size:11.5px;color:#374151;outline:none;padding:2px 0;min-width:60px}
        .sa-sinput::placeholder{color:#c0bcce}
        .sa-stepbar{display:inline-flex;align-self:center;flex-shrink:0;width:3px;min-height:28px;background:#7c3aed;border-radius:2px;margin:0 2px}

        /* Buttons */
        .sa-x{border:none;background:transparent;color:#b9a0cf;cursor:pointer;font-size:12px;padding:0 4px;line-height:1}
        .sa-x:hover{color:#dc2626}
        .sa-xs{font-size:10px}
        .sa-addstep{border:1px dashed #c4b5fd;background:#fff;color:#7c3aed;font-size:11px;font-weight:600;border-radius:8px;padding:6px 10px;cursor:pointer}
        .sa-addstep:hover{background:#f5f1fe}
        .sa-addgroup{border:1px dashed #a855f7;background:#faf5ff;color:#7c3aed;font-size:12.5px;font-weight:700;border-radius:10px;padding:10px 14px;cursor:pointer;display:block;width:100%;box-sizing:border-box}
        .sa-addgroup:hover{background:#f3e8ff}
      `}</style>
    </div>
    </div>
  );
}
